/**
 * The peripheral cue for work in flight ([[ROADMAP A.45]]).
 *
 * `ActiveWorkIndicator` is the surface that speaks — running, ready, failed,
 * with the reason. It sits bottom-left, and a reader at the top of a long page
 * may not catch it. This is the glance-sized companion: a dot on the rail
 * icon where the result will live. It says nothing the card does not; it
 * points at the card. One chain, three sizes — the dot, the card, the answer.
 *
 * Deliberately NOT a spinner and NOT a second announcement. A dot that pulses
 * while something runs, and turns green when a result is waiting to be seen,
 * is the whole vocabulary; the sentence stays on the card.
 */
import React from 'react';
import { Badge, keyframes } from '@mui/material';

import { useActiveJobsStore, visibleJobs } from '../../stores/activeJobsStore';

const pulse = keyframes`
  0%, 100% { opacity: 1; }
  50% { opacity: 0.35; }
`;

export type RailWorkState = 'idle' | 'running' | 'ready';

/** Pure: what the dot should say for a given job list. Running wins over
 *  ready — the reader can act on the ready one from the card either way, and
 *  a dot that flips green while another job is still going misreads. */
export function railWorkState(
  jobs: ReturnType<typeof useActiveJobsStore.getState>['jobs'],
  dismissed: string[],
): RailWorkState {
  const shown = visibleJobs(jobs, dismissed);
  if (shown.some((j) => j.status === 'running')) return 'running';
  if (shown.some((j) => j.status === 'done')) return 'ready';
  return 'idle';
}

const RailWorkDot: React.FC<{ children: React.ReactElement }> = ({ children }) => {
  const state = useActiveJobsStore((s) => railWorkState(s.jobs, s.dismissed));
  if (state === 'idle') return children;
  return (
    <Badge
      variant="dot"
      overlap="circular"
      aria-label={state === 'running' ? 'Work in progress' : 'A result is ready'}
      data-testid={`rail-work-dot-${state}`}
      sx={{
        '& .MuiBadge-badge': {
          // Green for both (owner, 2026-09-07: "for the 'in progress' dot use
          // a green dot, instead of black"). Running was `text.primary`, which
          // read as an error or a disabled state rather than as work.
          bgcolor: 'success.main',
          top: 12, right: 14, minWidth: 8, height: 8,
          // The two states differ by SHAPE, not only by motion: a hollow ring
          // while it runs, a filled dot when it is ready. The pulse below says
          // the same thing, but a reader with reduced motion turned on sees no
          // animation at all — MUI honours that media query — and two identical
          // green dots would then mean two different things.
          ...(state === 'running'
            ? { bgcolor: 'transparent', border: '2px solid', borderColor: 'success.main' }
            : {}),
          animation: state === 'running' ? `${pulse} 1.6s ease-in-out infinite` : 'none',
          '@media (prefers-reduced-motion: reduce)': { animation: 'none' },
        },
      }}
    >
      {children}
    </Badge>
  );
};

export default RailWorkDot;
